import React, { createContext, useContext, useEffect, useState } from 'react';

const StaxContext = createContext(null);

export const useStax = () => {
  const context = useContext(StaxContext);
  if (!context) {
    throw new Error('useStax must be used within a StaxProvider');
  }
  return context;
};

export const StaxProvider = ({ children, publicKey, environment = 'production' }) => {
  const [staxJs, setStaxJs] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadStaxJs = async () => {
      try {
        // Check if StaxJS is already loaded
        if (window.StaxJS) {
          const stax = window.StaxJS(publicKey, {
            environment: environment
          });
          setStaxJs(stax);
          setIsLoading(false);
          return;
        }

        // Load StaxJS script
        const script = document.createElement('script');
        script.src = environment === 'production' 
          ? 'https://js.staxpayments.com/v1/stax.js'
          : 'https://js.sandbox.staxpayments.com/v1/stax.js';
        script.async = true;

        script.onload = () => {
          if (window.StaxJS) {
            const stax = window.StaxJS(publicKey, {
              environment: environment
            });
            setStaxJs(stax);
            setIsLoading(false);
          } else {
            setError(new Error('StaxJS failed to load'));
            setIsLoading(false);
          }
        };

        script.onerror = () => {
          setError(new Error('Failed to load StaxJS script'));
          setIsLoading(false);
        };

        document.head.appendChild(script);

        // Cleanup function
        return () => {
          if (script.parentNode) {
            script.parentNode.removeChild(script);
          }
        };
      } catch (err) {
        setError(err);
        setIsLoading(false);
      }
    };

    if (publicKey) {
      loadStaxJs();
    } else {
      setError(new Error('Stax public key is required'));
      setIsLoading(false);
    }
  }, [publicKey, environment]);

  const tokenizeCard = async (cardData) => {
    if (!staxJs) {
      throw new Error('StaxJS is not initialized');
    }

    try {
      const response = await staxJs.tokenize({
        type: 'card',
        card: {
          number: cardData.cardNumber,
          exp_month: cardData.expMonth,
          exp_year: cardData.expYear,
          cvc: cardData.cvc,
          name: cardData.cardholderName,
          address_zip: cardData.zip
        }
      });

      if (response.error) {
        throw new Error(response.error.message || 'Tokenization failed');
      }

      return response.token;
    } catch (error) {
      console.error('Tokenization error:', error);
      throw error;
    }
  };

  const validateCard = (cardData) => {
    const errors = {};

    // Card number validation
    if (!cardData.cardNumber || cardData.cardNumber.replace(/\s/g, '').length < 13) {
      errors.cardNumber = 'Invalid card number';
    }

    // Expiration validation
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    
    if (!cardData.expMonth || cardData.expMonth < 1 || cardData.expMonth > 12) {
      errors.expMonth = 'Invalid month';
    }
    
    if (!cardData.expYear || cardData.expYear < currentYear) {
      errors.expYear = 'Invalid year';
    }
    
    if (cardData.expYear === currentYear && cardData.expMonth < currentMonth) {
      errors.expMonth = 'Card has expired';
    }

    // CVC validation
    if (!cardData.cvc || (cardData.cvc.length !== 3 && cardData.cvc.length !== 4)) {
      errors.cvc = 'Invalid security code';
    }

    // Cardholder name validation
    if (!cardData.cardholderName || cardData.cardholderName.trim().length < 2) {
      errors.cardholderName = 'Cardholder name is required';
    }

    // ZIP code validation (US format)
    if (cardData.zip && !/^\d{5}(-\d{4})?$/.test(cardData.zip)) {
      errors.zip = 'Invalid ZIP code';
    }

    return Object.keys(errors).length > 0 ? errors : null;
  };

  const value = {
    staxJs,
    isLoading,
    error,
    tokenizeCard,
    validateCard,
    environment
  };

  return (
    <StaxContext.Provider value={value}>
      {children}
    </StaxContext.Provider>
  );
};

export default StaxProvider;